/**
 * Tipe bersama antara orb-webgl-stage.ts (siklus hidup konteks) dan
 * orb-webgl-draw.ts (gambar per frame) — berkas terpisah supaya keduanya
 * tidak saling impor (siklus impor).
 */

import type { Rgb } from "@/components/assistant/orb/orb-colors";
import type { WebglOrbVariant } from "@/types/ai-assistant-orb";
import type { LiquidGlassUniforms } from "./orb-webgl-palette";

export interface WebglOrbRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

/** Satu program shader per jenis; semuanya berbagi satu konteks WebGL2. */
export type WebglShaderKind = "liquid-glass" | "liquid-morph";

/** Jenis shader untuk sebuah varian WebGL. */
export function webglShaderKindFor(variant: WebglOrbVariant): WebglShaderKind {
	return variant === "liquid-morph" ? "liquid-morph" : "liquid-glass";
}

/** Bagian keadaan yang sama untuk semua jenis; dibaca ULANG tiap frame. */
interface WebglOrbInstanceBase {
	/** null = elemen belum terpasang atau tak kelihatan; frame ini dilewati. */
	getRect(): WebglOrbRect | null;
	/** false = gerak dimatikan (reduced-motion/setelan aplikasi). */
	animate: boolean;
	/** true = idle lama, animasi dibekukan — sama seperti varian Canvas 2D. */
	frozen: boolean;
	/** Peredupan anti burn-in (idleBrightness), 0–1. */
	brightness: number;
}

export interface LiquidGlassInstanceState extends WebglOrbInstanceBase {
	kind: "liquid-glass";
	colorStops: readonly [Rgb, Rgb, Rgb, Rgb];
	uniforms: LiquidGlassUniforms;
}

/** liquid-morph: palet tetap, jadi tak ada warna/parameter — hanya tema. */
export interface LiquidMorphInstanceState extends WebglOrbInstanceBase {
	kind: "liquid-morph";
	/** true = tema terang (perlakuan B). */
	light: boolean;
}

export type WebglOrbInstanceState =
	| LiquidGlassInstanceState
	| LiquidMorphInstanceState;

/** Keadaan instance TANPA getRect (yang dibuat komponen): dibangun per render, dibaca lewat ref. */
export type WebglOrbInstanceData =
	| Omit<LiquidGlassInstanceState, "getRect">
	| Omit<LiquidMorphInstanceState, "getRect">;
